<template>
  <div class="application-comments-panel w-full min-w-0 max-w-full">
    <OurGroups class="mb-5">
      <OurGroup :title="$t('dealershipApplications.newCommentSection')">
        <OurTextarea
          v-model="newCommentText"
          :label="$t('dealershipApplications.newComment')"
          :placeholder="$t('dealershipApplications.newCommentPlaceholder')"
          rows="4"
          :maxlength="COMMENT_MAX_LENGTH"
          :disabled="submitting"
        />
        <p class="text-sm text-gray-500">
          {{
            $t("dealershipApplications.commentCharLimit", {
              current: normalizedComment.length,
              max: COMMENT_MAX_LENGTH,
            })
          }}
        </p>
        <OurButton
          class="mt-2"
          :text="$t('button.addComment')"
          :disabled="!canSubmit"
          :loading="submitting"
          @click="submitComment"
        />
      </OurGroup>
    </OurGroups>

    <DealershipApplicationsCommentsList
      :items="items"
      :pagination-data="paginationData"
      @pageChange="getData"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import apolloMixin from "@/mixins/apollo.mixin";
import DealershipApplicationsCommentsList from "@/views/settings/dealership-applications/index/_components/DealershipApplicationsCommentsList.vue";
import { customerApplicationCommentsQuery } from "@/views/settings/dealership-applications/index/gql/queries";
import { addCustomerApplicationCommentMutation } from "@/views/settings/dealership-applications/details/gql/mutations";
import DateService from "@/services/date.service";

const COMMENT_MAX_LENGTH = 2000;

export default {
  name: "ApplicationCommentsPanel",

  components: { DealershipApplicationsCommentsList },

  mixins: [apolloMixin],

  data() {
    return {
      COMMENT_MAX_LENGTH,
      newCommentText: "",
      items: [],
      paginationData: {},
      submitting: false,
    };
  },

  computed: {
    ...mapGetters("dealershipApplications", ["getStatus"]),

    normalizedComment() {
      return this.normalizeCommentBody(this.newCommentText);
    },

    canSubmit() {
      return this.normalizedComment.length > 0 && !this.submitting;
    },
  },

  created() {
    this.getData();
  },

  methods: {
    normalizeCommentBody(text) {
      if (typeof text !== "string") {
        return "";
      }

      return text.trim().replace(/\n{3,}/g, "\n\n");
    },

    statusLabel(statusKey) {
      const meta = this.getStatus(statusKey);

      return meta?.text || statusKey || "—";
    },

    async getData(page = Number.parseInt(this.$route.query.commentsPage, 10) || 1) {
      const { customerApplicationComments } = await this.$get(customerApplicationCommentsQuery, {
        customerApplicationId: this.$route.params.id,
        page,
        perPage: 20,
      });

      if (!customerApplicationComments) {
        this.items = [];
        this.paginationData = {};

        return;
      }

      this.items = customerApplicationComments.data.map((row) => {
        const { id, body, statusAtEvent, userFullName, userEmail, createdAt } = row;

        return {
          id,
          createdAt: DateService.dateConverter(createdAt, "datetime"),
          body: this.normalizeCommentBody(body),
          userData: {
            fullName: userFullName,
            email: userEmail,
          },
          statusAtEventLabel: this.statusLabel(statusAtEvent),
        };
      });

      this.paginationData = customerApplicationComments.paginatorInfo;
    },

    async submitComment() {
      if (!this.canSubmit) {
        return;
      }

      this.submitting = true;

      try {
        await this.$post(addCustomerApplicationCommentMutation, {
          id: this.$route.params.id,
          body: this.normalizedComment,
        });

        this.newCommentText = "";
        await this.getData(1);

        const query = { ...this.$route.query };

        delete query.commentsPage;

        if (this.$route.query.commentsPage) {
          await this.$router.replace({ name: this.$route.name, query });
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
